/**
 * https://github.com/europace/baufismart-kundenangaben-api
 */
import LocalStorage from './LocalStorage';
import { IRequestError } from './RequestManager';
import RequestResponse from './RequestResponse';

import { components } from '../interfaces/BaufiLeadApi';
import { ILoanRequestAddSubmit } from './LoanRequest';
import IHome from '../interfaces/IHome';
import { propertyType2Text } from './PropertyRequest';
import { incomeList, timeList } from '../components/LoanCalculator/LoanUserDetails';
import Config from './Config';
import PropApiRequest from './PropApiRequest';
import { ILoanOffer } from './BaufiSmartApi';
import omitEmpty from 'omit-empty';
import { getTypeAndLocationText } from '../components/SearchResult/HomeCard';

const BASE_URL = Config.get('baseUrl').replace(/\/$/, ''); // without trailing slash
const useDnzProxy = true;
const isTestModus = Config.get('environment') !== 'prod';
// const API_URL = 'https://baufinanzierung.api.europace.de/kundenangaben/body-validation'; // validation without saving
const API_URL = useDnzProxy ? BASE_URL + '/europace/baufismart-kundenangaben-api' : 'https://baufinanzierung.api.europace.de';
const LEAD_API_URL = Config.get('environment') === 'dev' ? API_URL + '/kundenangaben/body-validation' : API_URL + '/kundenangaben';

type IBaufiLeadRequest = components['schemas']['ImportKundenangabenRequest'];

type IPropertyTypeKey = keyof typeof propertyType2Text;

type IBaufiLeadResponse = {
    vorgangsnummer: string;
};

type IBaufiTokenResponse = {
    access_token: string;
    scope: string;
    token_type: string; // 'Bearer';
    expires_in: number; // 3600;
};

class BaufiLeadApi {
    protected token: string = null;
    protected tokenExpires: number = null;
    protected LoadingCredentialsPromise = null;

    public addLead = (data: ILoanRequestAddSubmit, loanRequestId: string): Promise<IBaufiLeadResponse> => {
        return this.getToken().then((token) => {
            return this.request('POST', LEAD_API_URL, this.lead2baufi(data, loanRequestId)).then((res) => {
                return res.getData();
            });
        });
    };

    protected lead2baufi = (data: ILoanRequestAddSubmit, loanRequestId: string): IBaufiLeadRequest => {
        const loan = data.loanCalculator || {};

        // const vorgangsId = `dnz_${data.property.id ? data.property.id : 'noPropertyId'}_${new Date().toISOString()}`;
        const vorgangsId = `dnz_${loanRequestId}`;
        const home = data.property || ({} as IHome);
        const offer = data.offer || ({} as ILoanOffer);

        const typeAndLocationText = getTypeAndLocationText(home);

        const propertyText = home?.id
            ? `# Ausgewählte Immobilie auf wohnglueck.de
id: ${home.id}
url: https://immobilien.wohnglueck.de/property/${home.id}
titel: ${typeAndLocationText}
preis: ${home.priceOnDemand ? 'Auf Anfrage' : home.price}
wohnfläche: ${home.livingArea}
grundstücksfläche: ${home.plotArea}
zimmer: ${home.chambers}`
            : `# Keine Immobilie auf wohnglueck.de ausgewählt`;

        const offerText = offer.baufiId
            ? `# ausgewähltes Darlehen
produktAnbieter: ${offer.bankName}
darlehensId: ${offer.baufiId}
sollZins: ${offer.sollZins}
effektivZins: ${offer.effektivZins}
rateMonatlich: ${offer.monatlicheRate}
restschuldNachZinsBindungsEnde: ${offer.restSchuld}
gesamtkosten: ${offer.gesamtBetrag}
gesamtlaufzeitInJahren: ${Math.round((offer.gesamtLaufzeit / 12) * 100) / 100}
machbarkeit: ${offer.machbarkeit}`
            : '# kein Darlehen ausgewählt';

        const leadData: IBaufiLeadRequest = {
            kundenangaben: {
                finanzierungsbedarf: {
                    finanzierungszweck: {
                        '@type': 'KAUF', // NEUBAU,  KAUF_NEUBAU_VOM_BAUTRAEGER
                        kaufpreis: loan.price,
                        nebenkosten: {
                            maklergebuehr: {
                                wert: loan.findersFeePercentage,
                                einheit: 'PROZENT',
                            },
                            notargebuehr: {
                                wert: loan.notarialCostsPercentage + loan.landRegisterCostsPercentage,
                                einheit: 'PROZENT',
                            },
                            grunderwerbsteuer: {
                                wert: loan.stampDutyPercentage,
                                einheit: 'PROZENT',
                            },
                        },
                    },
                    praeferenzen: {
                        finanzierungsdetailspraeferenzen: {
                            zinsbindung: {
                                '@type': 'ZINSBINDUNG_IN_JAHREN',
                                jahre: loan.fixedInterestRateYears,
                            },
                        },
                        zeitlicherRahmen: {
                            '@type': data.loanDetails.time === '0' ? 'SO_SCHNELL_WIE_MOEGLICH' : 'KEINE_ZEITLICHER_RAHMEN_PRAEFERENZ',
                            kommentar: timeList[data.loanDetails.time],
                        },
                    },
                    finanzierungsbausteine: [
                        {
                            '@type': 'ANNUITAETENDARLEHEN',
                            darlehensbetrag: loan.price + (loan.closingCostsPercentage * loan.price) / 100 - loan.equity,
                            annuitaetendetails: {
                                zinsbindungInJahren: loan.fixedInterestRateYears,
                                tilgungswunsch: {
                                    '@type': 'TILGUNG_IN_PROZENT',
                                    tilgungssatzInProzent: loan.amortizationRatePercentage,
                                },
                            },
                        },
                    ],
                },
                finanzierungsobjekt: {
                    immobilie: {
                        adresse: {
                            strasse: home.address?.address_line1,
                            plz: home.address?.postal_code,
                            ort: home.address?.locality,
                        },
                        // evtl. TODO: typ, stellplaetze
                        typ: {
                            '@type': this.propertyType2immobilieType(home),
                            grundstuecksgroesse: home.plotArea,
                            gebaeude: {
                                baujahr: home.baujahr,
                                // anzahlGeschosse // TODO
                                nutzung: {
                                    wohnen: {
                                        gesamtflaeche: home.livingArea,
                                        nutzungsart: data.loanDetails.usage
                                            ? {
                                                '@type': data.loanDetails.usage, // "EIGENGENUTZT" | "VERMIETET" | "TEILVERMIETET"
                                            }
                                            : null,
                                    },
                                },
                            },
                        },
                    },
                },
                haushalte: [
                    {
                        kunden: [
                            {
                                referenzId: `dnz_kunde_${new Date().toISOString()}`,
                                personendaten: {
                                    person: {
                                        // anrede: data.userData?.
                                        vorname: data.userData?.given_name,
                                        nachname: data.userData?.family_name,
                                        // x: data.userData?.
                                    },
                                },
                                wohnsituation: {
                                    anschrift: {
                                        strasse: data.userData?.address_line1,
                                        // hausnummer:
                                        plz: data.userData?.postal_code,
                                        ort: data.userData?.locality,
                                    },
                                },
                                kontakt: {
                                    email: data.userData?.mail,
                                    telefonnummer: {
                                        vorwahl: '', // TODO?
                                        nummer: data.userData?.phone,
                                    },
                                },
                                finanzielles: {
                                    // beschaeftigung: data.loanDetails.occupation,
                                    beschaeftigung: {
                                        '@type': data.loanDetails.occupation,
                                    },
                                },
                                // zusatzangaben: data.loanDetails.occupation
                                //     ? {
                                //           zusatzangabenProProduktanbieter: [
                                //               {
                                //                   '@type': 'ZUSATZANGABEN_KUNDE_ING',
                                //                   berufsgruppe: data.loanDetails.occupation,
                                //               },
                                //           ],
                                //       }
                                //     : null,
                            },
                        ],
                        // finanzielleSituation: {
                        //     einnahmen: {
                        //         summeSonstigeEinnahmenMonatlich: {
                        //             betrag: data.loanDetails.income,
                        //             kommentar: `Haushaltsnettoeinkommen pro Monat: ${incomeList[data.loanDetails.income]}`,
                        //         },
                        //     },
                        // },
                    },
                ],
            },
            importMetadaten: {
                datenkontext: isTestModus ? 'TEST_MODUS' : 'ECHT_GESCHAEFT',
                externeVorgangsId: vorgangsId,
                tippgeber: {
                    tippgeberPartnerId: 'QEI21',
                },
                betreuung: {
                    kundenbetreuer: 'QEI21',
                    bearbeiter: 'QEI21',
                },
                zusaetzlicherEreignistext: `
${propertyText}

${offerText}
`,
            },
        };

        return omitEmpty(leadData);
    };

    /* BAUGRUNDSTUECK, EIGENTUMSWOHNUNG, DOPPELHAUSHAELFTE, EINFAMILIENHAUS, ZWEIFAMILIENHAUS, MEHRFAMILIENHAUS, REIHENHAUS, IMMOBILIE_OHNE_TYP */
    protected propertyType2immobilieType = (prop: IHome): components['schemas']['Immobilientyp']['@type'] => {
        const type = (Object.keys(propertyType2Text) as IPropertyTypeKey[]).find((key) => propertyType2Text[key] === prop.propertyTypeText);

        if (type?.startsWith('apartment')) {
            return 'EIGENTUMSWOHNUNG';
        }

        switch (type) {
            case 'house':
            case 'house_bungalow':
            case 'house_farm':
            case 'house_one_family':
            case 'house_other':
            case 'house_villa':
                return 'EINFAMILIENHAUS';
            case 'house_multi_family':
                return 'MEHRFAMILIENHAUS';
            case 'house_row':
                return 'REIHENHAUS';
            case 'house_semi_detached':
                return 'DOPPELHAUSHAELFTE';
        }

        return 'IMMOBILIE_OHNE_TYP';
    };

    // protected occupation2berufsgruppe = (
    //     str: ILoanRequestAddSubmit['loanDetails']['occupation']
    // ): components['schemas']['ZusatzangabenKundeIng']['berufsgruppe'] => {
    //     /* "ANGESTELLTER" | "ARBEITER" | "BEAMTER" | "FREIBERUFLER" | "SELBSTSTAENDIG" | "RENTNER" | "HAUSMANN" | "PRIVATIER" | "SOLDAT" | "SCHUELER" | "AZUBI" | "STUDENT" | "OHNE"*/

    //     return null;
    // };

    protected getToken = (forceRenew: boolean = false): Promise<string> => {
        if (!forceRenew && this.token && this.tokenExpires > Date.now()) {
            // token from class
            return Promise.resolve(this.token);
        } else {
            // token from local storage
            const savedToken: { token: string; tokenExpires: number } = LocalStorage.getItem('BaufiLeadApiToken');
            if (!forceRenew && savedToken?.tokenExpires - 100 > Date.now()) {
                this.token = savedToken.token;
                this.tokenExpires = savedToken.tokenExpires;
                return Promise.resolve(savedToken.token);
            } else {
                // token from API
                return this.getTokenFromApi(forceRenew).then((res) => {
                    const data: IBaufiTokenResponse = res.getData()['baufismart-kundenangaben-api'];
                    this.token = data.access_token;
                    this.tokenExpires = Date.now() + data.expires_in * 1000;
                    LocalStorage.setItem('BaufiLeadApiToken', { token: this.token, tokenExpires: this.tokenExpires });
                    return Promise.resolve(this.token);
                });
            }
        }
    };

    protected getTokenFromApi = (forceRenew: boolean = false) => {
        if (!this.LoadingCredentialsPromise) {
            this.LoadingCredentialsPromise = PropApiRequest.getRequest(
                BASE_URL + '/prop-api/europace/credentials?api[]=baufismart-kundenangaben-api' + (forceRenew ? '&force-renew=1' : '')
            )
                .then((res) => {
                    this.LoadingCredentialsPromise = null;
                    return res;
                })
                .catch((e) => {
                    console.error(e);
                    this.LoadingCredentialsPromise = null;
                    return Promise.reject(e);
                });
        }
        return this.LoadingCredentialsPromise;
    };

    protected request = (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS', url: string, data?: any) => {
        return new Promise<RequestResponse>((resolve: (resp: RequestResponse) => void, reject: (resp: IRequestError) => void) => {
            const xhr = new XMLHttpRequest();
            xhr.open(method, url);
            xhr.onerror = (e: any) => {
                const resp = new RequestResponse(xhr.status, url);
                console.error('XHR Error:', xhr);
                let data = null;
                try {
                    data = JSON.parse(xhr.responseText);
                } catch (e) {
                    console.error(e);
                }
                reject({ ...resp, responseData: data });
            };

            xhr.ontimeout = () => {
                console.error('timeout for request', url);
            }

            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHttpRequest.DONE) {
                    const resp = new RequestResponse(xhr.status, url);
                    if (xhr.status >= 200 && xhr.status <= 299) {
                        try {
                            resp.setData(JSON.parse(xhr.responseText));
                        } catch (e) {
                            resp.setData(xhr.responseText);
                        }
                        resolve(resp);
                    } else {
                        console.error('Request error', xhr.status, resp);
                        let data = null;
                        try {
                            data = JSON.parse(xhr.responseText);
                        } catch (e) {
                            console.error(e);
                        }
                        reject({ ...resp, responseData: data });
                    }
                }
            };

            // xhr.setRequestHeader('authorization', `Bearer ${this.token}`);
            xhr.setRequestHeader(useDnzProxy ? 'x-authorization' : 'authorization', `Bearer ${this.token}`);
            xhr.setRequestHeader('cache-control', `no-cache`);
            xhr.setRequestHeader('content-type', `application/json`);
            xhr.setRequestHeader('X-TraceId', `dnz_${Date.now()}`);
            xhr.timeout = 30000;
            xhr.send(data ? JSON.stringify(data) : null);
        });
    };
}

export default new BaufiLeadApi();
